// MobileSuggestionsTab — Vorschläge-Reiter des mobilen Hinzufügen-Modus: Themen-Zeile des
// Decks, Rangliste der Vorschlags-Maschine als Kachel-Raster (ohne Sortier-Menü, die
// Reihenfolge IST das Ergebnis), gedockte Filterleiste, Viewer über der Liste und das
// Langdruck-Menü zum Ausblenden einer Karte bzw. Stummschalten eines ihrer Themen.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Badge from '../../../../ui/Badge';
import CardGrid from '../../../../ui/CardGrid';
import Divider from '../../../../ui/Divider';
import Skeleton from '../../../../ui/Skeleton';
import { useLoadMoreSentinel } from '../../../../hooks/useLoadMoreSentinel';
import CardBrowserFilterRow from '../../components/CardBrowserFilterRow';
import SuggestionListState from '../../catalog/SuggestionListState';
import SuggestionThemeRow from '../../catalog/SuggestionThemeRow';
import { popularDividerIndex } from '../../catalog/profileThemes';
import { useDeckSuggestions, useDropSuggestionFromCache } from '../../catalog/useDeckSuggestions';
import { useSuggestionSettings } from '../../catalog/useSuggestionSettings';
import '../../catalog/suggestionReasons.css';
import { useHistoryLayer } from '../hooks/useHistoryLayer';
import AddResultsViewer from './AddResultsViewer';
import MobileResultTile from './MobileResultTile';
import SuggestionActionSheet from './SuggestionActionSheet';

/**
 * @param {number} numericDeckId — numerische ID des virtuellen Decks
 * @param {object[]} deckCards — Karten des Decks, für den „✓ im Deck"-Abgleich per oracleId
 * @param {boolean} canAdd — Plus-Knöpfe, Viewer-Knopf und Langdruck-Menü (eigenes,
 *   bearbeitbares Deck)
 * @param {object} filters — Filter-Objekt; liegt im Rahmen, weil `Tabs.Panel` den inaktiven
 *   Reiter abbaut
 * @param {(patch: object) => void} onFiltersChange — Teil-Änderung des Filter-Objekts
 * @param {(entry) => Promise<{entryId?: number, duplicate: boolean}|null>} addEntry —
 *   gemeinsamer Add-Pfad der Page (Toasts, Undo-Registrierung, Ziel-Handhabung)
 */
export default function MobileSuggestionsTab({
  numericDeckId,
  deckCards = [],
  canAdd = false,
  filters,
  onFiltersChange,
  addEntry,
}) {
  const {
    profile,
    entries,
    available,
    status,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useDeckSuggestions(numericDeckId, { filters });

  const sentinelRef = useLoadMoreSentinel({ hasNextPage, isFetchingNextPage, fetchNextPage });

  const {
    hiddenCards,
    hideCard,
    hidingOracleIds,
    unhideCard,
    muteTag,
    unmuteTag,
  } = useSuggestionSettings(numericDeckId);

  const dropSuggestion = useDropSuggestionFromCache(numericDeckId);

  const {
    isOpen: isViewerOpen,
    payload: viewerPayload,
    open: openViewer,
    close: closeViewer,
  } = useHistoryLayer('add-viewer');

  const deckOracleIds = useMemo(
    () => new Set((deckCards || []).map((card) => card.oracleId).filter(Boolean)),
    [deckCards]
  );

  const trennIndex = popularDividerIndex(entries);

  const handleTap = useCallback((entry) => {
    const index = entries.findIndex((e) => e.id === entry.id);
    openViewer({ index: index < 0 ? 0 : index });
  }, [entries, openViewer]);

  // ── Hinzufügen aus Kachel und Viewer ─────────────────────────────────────
  // Solange ein Add läuft, löst ein weiteres Plus nichts aus: der Ref sperrt schon im selben
  // Tick, der State färbt die Kachel. Nach Erfolg geht die Kachel sofort aus dem Cache — der
  // Add-Pfad der Page invalidiert nur, und der Refetch scort den ganzen Kandidatenkreis neu.
  const [addingId, setAddingId] = useState(null);
  const addingRef = useRef(null);
  // Vor/Zurück zwischen zwei Editor-Adressen lässt diese Komponente stehen; ein Add des
  // vorigen Decks darf das neue nicht sperren.
  useEffect(() => {
    addingRef.current = null;
    setAddingId(null);
  }, [numericDeckId]);

  const handleAdd = useCallback(async (entry) => {
    if (addingRef.current !== null) return;
    addingRef.current = entry.id;
    setAddingId(entry.id);
    try {
      const result = await addEntry(entry);
      if (result && !result.duplicate) dropSuggestion({ id: entry.id, oracleId: entry.oracleId });
    } finally {
      if (addingRef.current === entry.id) {
        addingRef.current = null;
        setAddingId(null);
      }
    }
  }, [addEntry, dropSuggestion]);

  // ── Langdruck-Menü ───────────────────────────────────────────────────────
  // Das Sheet schließt erst nach Erfolg der Mutation; bis dahin sperrt `sheetBusy` seine
  // Zeilen und jeden weiteren Langdruck — sonst schlösse der Erfolg der laufenden Mutation
  // ein inzwischen für eine andere Karte geöffnetes Sheet. Die Hooks liefern `mutate`, die
  // Callbacks laufen deshalb über das zweite Argument. Unmountet der Reiter während der
  // Mutation, feuern diese Aufruf-Callbacks nicht; das Sheet unmountet mit, nichts bleibt
  // offen oder gesperrt.
  const [sheetEntry, setSheetEntry] = useState(null);
  const [sheetBusy, setSheetBusy] = useState(false);

  const handleLongPress = useCallback((entry) => {
    if (sheetBusy) return;
    setSheetEntry(entry);
  }, [sheetBusy]);

  const sheetCallbacks = {
    onSuccess: () => setSheetEntry(null),
    onSettled: () => setSheetBusy(false),
  };

  const handleHide = (entry) => {
    setSheetBusy(true);
    hideCard(entry.oracleId, sheetCallbacks);
  };

  const handleMuteTag = (tagId) => {
    setSheetBusy(true);
    muteTag(tagId, sheetCallbacks);
  };

  return (
    <>
      <div className="mobile-add-themes">
        <SuggestionThemeRow
          profile={profile}
          entries={entries}
          hiddenCards={hiddenCards}
          unhideCard={unhideCard}
          muteTag={muteTag}
          unmuteTag={unmuteTag}
          showProfile={status === 'success' && available !== false}
        />
      </div>

      <SuggestionListState
        status={status}
        error={error}
        available={available}
        isEmpty={entries.length === 0}
        onRetry={() => refetch()}
        skeleton={(
          <CardGrid variant="touch">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height="180px" />
            ))}
          </CardGrid>
        )}
      >
        <CardGrid variant="touch">
          {entries.map((entry, index) => (
            <React.Fragment key={entry.id}>
              {index === trennIndex && (
                <CardGrid.Full>
                  <Divider>Weitere beliebte Karten</Divider>
                </CardGrid.Full>
              )}
              <MobileResultTile
                entry={entry}
                inDeck={Boolean(entry.oracleId && deckOracleIds.has(entry.oracleId))}
                canAdd={canAdd}
                busy={addingId === entry.id || hidingOracleIds.has(entry.oracleId)}
                onTap={handleTap}
                onLongPress={canAdd ? handleLongPress : undefined}
                onAdd={handleAdd}
              >
                <span className="suggestion-reasons">
                  {(entry.reasons || []).map((grund) => (
                    <Badge key={grund.tagId} title={grund.description || undefined}>{grund.label}</Badge>
                  ))}
                </span>
              </MobileResultTile>
            </React.Fragment>
          ))}
        </CardGrid>
        <div ref={sentinelRef} aria-hidden />
      </SuggestionListState>

      <CardBrowserFilterRow
        filters={filters}
        onChange={onFiltersChange}
        showSort={false}
        showOwnedOnly
      />

      <AddResultsViewer
        isOpen={isViewerOpen}
        payloadIndex={viewerPayload?.index}
        onClose={closeViewer}
        entries={entries}
        status={status}
        canAdd={canAdd}
        onAdd={handleAdd}
        deckOracleIds={deckOracleIds}
      />

      <SuggestionActionSheet
        entry={sheetEntry}
        open={sheetEntry != null}
        onOpenChange={(open) => { if (!open) setSheetEntry(null); }}
        onHide={handleHide}
        onMuteTag={handleMuteTag}
        busy={sheetBusy}
      />
    </>
  );
}
